/**
 * Behavioral Tracking Service
 * Phase 9.5.2b: Logs user interactions for adaptive Smart Swirl learning
 * Tracks views, likes, adds to collections, and dismisses to improve recommendations
 */

import { supabase } from '../lib/supabase';
import { FLAGS } from '../lib/flags';

/**
 * Log a user interaction with an item
 * @param {string} userId - User ID
 * @param {string} itemId - Item ID that was interacted with
 * @param {string} type - Interaction type: 'view', 'like', 'add_to_collection', 'dismiss'
 * @param {string|null} sourceSwirlId - Optional Smart Swirl ID that generated this item
 * @returns {Promise<void>}
 */
export async function logUserInteraction(userId, itemId, type, sourceSwirlId = null) {
  // Check if behavioral learning is enabled
  if (!FLAGS.BEHAVIORAL_LEARNING_ENABLED) {
    return;
  }

  if (!userId || !itemId || !type) {
    if (import.meta.env.DEV) {
      console.warn('⚠️ Invalid parameters for logUserInteraction:', { userId, itemId, type });
    }
    return;
  }

  // Validate interaction type
  const validTypes = ['view', 'like', 'add_to_collection', 'dismiss'];
  if (!validTypes.includes(type)) {
    if (import.meta.env.DEV) {
      console.warn(`⚠️ Invalid interaction type: ${type}. Must be one of: ${validTypes.join(', ')}`);
    }
    return;
  }

  try {
    const payload = {
      user_id: userId,
      item_id: itemId,
      interaction_type: type,
      source_swirl_id: sourceSwirlId || null,
    };

    const { error } = await supabase
      .schema('deepsearch').from('behavioral_signals')
      .insert([payload]);

    if (error) {
      // Handle missing table gracefully (for development/testing)
      if (error.code === 'PGRST116' || error.code === 'PGRST205' || error.message?.includes('does not exist')) {
        if (import.meta.env.DEV) {
          console.warn('⚠️ Behavioral signals table not available (expected if migration not run)');
        }
        return;
      }
      throw error;
    }

    if (import.meta.env.DEV) {
      console.log(`📊 Logged ${type} interaction for item ${itemId.slice(0, 8)}...`);
    }
  } catch (error) {
    // Fail silently to not disrupt UX
    if (import.meta.env.DEV) {
      console.error('Error logging user interaction:', error);
    }
  }
}

/**
 * Get behavioral summary for a user
 * @param {string} userId - User ID
 * @param {number} limit - Maximum number of signals to fetch (default: 100)
 * @returns {Promise<Array>} Array of behavioral signals
 */
export async function getBehaviorSummary(userId, limit = 100) {
  if (!FLAGS.BEHAVIORAL_LEARNING_ENABLED) {
    return [];
  }

  if (!userId) {
    return [];
  }

  try {
    const { data, error } = await supabase
      .schema('deepsearch').from('behavioral_signals')
      .select('interaction_type, source_swirl_id, item_id, created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      if (error.code === 'PGRST116' || error.code === 'PGRST205' || error.message?.includes('does not exist')) {
        return [];
      }
      throw error;
    }

    return data || [];
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Error fetching behavior summary:', error);
    }
    return [];
  }
}

/**
 * Get behavioral signals for a specific Smart Swirl
 * @param {string} userId - User ID
 * @param {string} swirlId - Smart Swirl ID
 * @returns {Promise<Array>} Array of behavioral signals for this swirl
 */
export async function getSwirlBehaviorSignals(userId, swirlId) {
  if (!FLAGS.BEHAVIORAL_LEARNING_ENABLED) {
    return [];
  }

  if (!userId || !swirlId) {
    return [];
  }

  try {
    const { data, error } = await supabase
      .schema('deepsearch').from('behavioral_signals')
      .select('interaction_type, item_id, created_at')
      .eq('user_id', userId)
      .eq('source_swirl_id', swirlId)
      .order('created_at', { ascending: false });

    if (error) {
      if (error.code === 'PGRST116' || error.code === 'PGRST205') {
        return [];
      }
      throw error;
    }

    return data || [];
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Error fetching swirl behavior signals:', error);
    }
    return [];
  }
}

